import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Box, Button } from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import dayjs from 'dayjs';

import * as taxApi from '../../lib/api/taxbill';
import TaxbillList from '../../components/taxbillmng/TaxbillList';
import BreadcrumbsContainer from '../common/breadcurmbs/BreadcrumbsContainer';
import AskDialog from '../../components/common/dialog/AskDialog';

import { CollectBuyListBox } from '../../components/collects_buys/CollectBuyListBox';
import CollectBuyStatusList from '../../components/collects_buys/CollectBuyStatusList';
import {
  listCollectsBuys,
  setCollectsBuysFilters,
  resetCollectsBuysFilters,
} from '../../modules/taxbillModule/collects-buys';
import TaxbillTotalSummary from '../../components/taxbillmng/TaxbillTotalSummary';

const CollectBuyListContainer = () => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  // 홈 위젯(지급 예정)에서 ?billTyp=/&reqCompYn=/&dateField=/&strtDt=/&endDt= 로 들어오면
  // 그 필터로 초기 조회한다 - 카드=목록 건수를 맞추기 위한 최소 딥링크 지원(메인화면 재검토,
  // 2026-10-23). 최초 마운트 값만 읽는다.
  const [searchParams] = useSearchParams();

  const { totalPrice, collects_buys, defaultFilters, error, loading } =
    useSelector(({ taxbillModule, loading }) => ({
      totalPrice: taxbillModule.collectsBuys.totalPrice,
      collects_buys: taxbillModule.collectsBuys.collects_buys,
      defaultFilters: taxbillModule.collectsBuys.defaultFilters,
      error: taxbillModule.collectsBuys.error,
      loading: loading['taxbill/LIST_COLLECTS_BUYS'],
    }));

  const [tableData, setTableData] = useState([]);

  const makeFilters = (filters) => {
    const startDate =
      filters.searchStrtDt !== null && filters.searchStrtDt !== ''
        ? dayjs(filters.searchStrtDt).format('YYYY-MM-DD')
        : '';
    const endDate =
      filters.searchEndDt !== null && filters.searchEndDt !== ''
        ? dayjs(filters.searchEndDt).format('YYYY-MM-DD')
        : '';

    let searchData = {
      invTyp: filters.searchInvTyp,
      searchTyp: filters.searchTyp,
      reqCompYn: filters.searchYn,
      procCompYn: filters.procCompYn,
      [filters.searchKeyword]: filters.searchWords,
      strtDt: startDate,
      endDt: endDate,
      // 홈 위젯 딥링크 필터(메인화면 재검토, 2026-10-23).
      dateField: filters.searchDateField || undefined,
    };

    return searchData;
  };

  // 마운트 시 1회만 홈 위젯 딥링크 쿼리스트링을 읽어 defaultFilters에 반영한다.
  useEffect(() => {
    const overrides = {};
    const billTyp = searchParams.get('billTyp');
    const reqCompYn = searchParams.get('reqCompYn');
    // 재검토 2(Fable, 2026-10-23) B2: 이 화면의 기본값(defaultFilters.procCompYn='Y', "지급완료"만
    // 보여주는 화면 기본)을 그대로 두면 지급 예정 카드(procCompYn=N)에서 들어와도 "완료" 목록이
    // 열렸다 - 홈 위젯 링크가 명시한 procCompYn을 읽어 덮어쓴다.
    const procCompYn = searchParams.get('procCompYn');
    const strtDt = searchParams.get('strtDt');
    const endDt = searchParams.get('endDt');
    const dateField = searchParams.get('dateField');
    if (billTyp) overrides.searchInvTyp = billTyp;
    if (reqCompYn) overrides.searchYn = reqCompYn;
    if (procCompYn) overrides.procCompYn = procCompYn;
    if (strtDt) overrides.searchStrtDt = strtDt;
    if (endDt) overrides.searchEndDt = endDt;
    if (dateField) overrides.searchDateField = dateField;
    const deepLinkApplied = Object.keys(overrides).length > 0;
    if (deepLinkApplied) {
      dispatch(setCollectsBuysFilters({ ...defaultFilters, ...overrides }));
    }
    // 재검토 2(Fable, 2026-10-23) §3/§6-4: 이 화면을 떠날 때 딥링크로 덮어쓴 필터(특히
    // procCompYn='N')를 기본값으로 되돌린다 - 안 그러면 redux(taxbillModule)에 남아 다음에
    // 메뉴로 들어왔을 때도 "지급 예정만" 보이는 상태가 말없이 유지된다.
    return () => {
      if (deepLinkApplied) {
        dispatch(resetCollectsBuysFilters());
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch(listCollectsBuys(makeFilters(defaultFilters)));
  }, [dispatch, defaultFilters]);

  useEffect(() => {
    if (collects_buys) {
      setTableData(collects_buys);
    }
  }, [collects_buys]);

  const handleFilters = useCallback(
    (name, value) => {
      const newFilters = { ...defaultFilters, [name]: value };

      dispatch(setCollectsBuysFilters(newFilters));
    },
    [defaultFilters, dispatch],
  );

  const columns = useMemo(
    () => [
      { field: 'id', hide: true, sortable: false, editable: false },
      { field: 'invUuid', hide: true, sortable: false, editable: false }, //세금계산서ID
      { field: 'roUuid', hide: true, sortable: false, editable: false }, //수주ID
      { field: 'billUuid', hide: true, sortable: false, editable: false }, //거래ID
      { field: 'prjUuid', hide: true, sortable: false, editable: false }, //프로젝트ID
      {
        field: 'invYn',
        hide: true,
        editable: false,
      },
      {
        field: 'invNo',
        headerName: '계산서번호',
        hide: true,
        editable: false,
      },
      {
        field: 'invTyp',
        headerName: '유형',
        hide: true,
        editable: false,
      },
      {
        field: 'reqCompDt',
        headerName: '발행일',
        description: '',
        align: 'center',
        headerAlign: 'center',
        type: 'date',
        // valueGetter 삭제(2026-10-21): MUI X v7 시그니처는 (value, row, ...) 라서 value 자체가
        // 날짜 문자열인데, 예전 v5/v6 스타일로 value.value 를 읽고 있어 항상 undefined -> 늘 null로
        // 찍혀 날짜가 안 보였다(크래시는 안 났지만 빈칸). ResponsiveDataGrid 공통 보정(날짜 칸인데
        // valueGetter 없으면 자동으로 안전 변환)에 맡긴다.
        flex: 0.8,
        minWidth: 150,
      },
      {
        field: 'partnerNm',
        headerName: '고객명',
        minWidth: 200,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'left',
        headerAlign: 'center',
      },
      {
        field: 'cstNm',
        headerName: '사업명',
        minWidth: 200,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'left',
        headerAlign: 'center',
      },
      {
        field: 'salesUsrNm',
        headerName: '영업 사원',
        description: '영업 사원',
        type: 'string',
        flex: 0.8,
        minWidth: 120,
      },
      {
        field: 'invTxt',
        headerName: '발급내용',
        minWidth: 200,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'invAmt',
        headerName: '계산서 금액',
        minWidth: 150,
        flex: 1,
        type: 'number',
      },
      {
        field: 'osTran',
        headerName: '해외 거래',
        minWidth: 100,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'reqPlanUsrNm',
        headerName: '발행요청자',
        description: '발행요청자',
        type: 'string',
        flex: 0.8,
        minWidth: 120,
      },
      {
        field: 'procCompDt',
        headerName: '지급(수금)완료일자',
        description: '',
        align: 'center',
        headerAlign: 'center',
        flex: 0.8,
        minWidth: 180,
      },
      {
        field: 'procCompYn',
        headerName: '지급(수금)완료여부',
        description: '',
        align: 'center',
        headerAlign: 'center',
        flex: 0.8,
        minWidth: 180,
      },
      {
        field: 'action',
        headerName: '취소',
        minWidth: 100,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <Button
            size="small"
            variant="contained"
            color="error"
            onClick={() => handleRemove(params.row)}
          >
            취소
          </Button>
        ),
      },
    ],
    [],
  );

  //검색 관련 사항
  const initialValues = {
    searchKeyword: defaultFilters.searchKeyword,
    searchWords: defaultFilters.searchWords,
    searchStrtDt: defaultFilters.searchStrtDt,
    searchEndDt: defaultFilters.searchEndDt,
  };

  const validationSchema = yup.object({
    searchKeyword: yup.string('').nullable(),
    searchWords: yup.string('').trim().nullable(),
    searchStrtDt: yup.date().nullable().default(undefined),
    searchEndDt: yup
      .date()
      .min(yup.ref('searchStrtDt'), '검색 종료일을 확인하세요')
      .when('searchStrtDt', (searchStrtDt, schema) => {
        if (searchStrtDt) return schema.required('검색 종료일을 입력하세요');
      })
      .nullable()
      .default(undefined),
  });

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(true);
      const searchData = {
        searchInvTyp: defaultFilters.searchInvTyp,
        searchTyp: defaultFilters.searchTyp,
        searchYn: defaultFilters.searchYn,
        procCompYn: defaultFilters.procCompYn,
        searchKeyword: values.searchKeyword,
        searchWords: values.searchWords,
        searchStrtDt: values.searchStrtDt,
        searchEndDt: values.searchEndDt,
      };
      dispatch(setCollectsBuysFilters(searchData));
      setTimeout(() => {
        setSubmitting(false);
      }, 1000);
    },
  });

  //수금,지금 취소
  const [selectedItem, setSelectedItem] = useState(null);
  const [removeVisible, setRemoveVisible] = useState(false);
  const handleRemove = useCallback((row) => {
    setRemoveVisible(true);
    setSelectedItem(row);
  }, []);
  const onRemoveCancle = () => {
    setRemoveVisible(false);
    setSelectedItem(null);
  };
  const onRemoveConfirm = () => {
    //매출 취소
    onRemove();
    setRemoveVisible(false);
    setSelectedItem(null);
  };

  const onRemove = async () => {
    try {
      const response = await taxApi.onCollectBuyCancle(selectedItem?.invUuid);
      if (response.data.info) {
        dispatch(listCollectsBuys(makeFilters(defaultFilters)));
        enqueueSnackbar('취소 되었습니다', {
          variant: 'success',
        });
      }
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
  };

  return (
    <>
      <BreadcrumbsContainer
        title="채권&채무 관리/수금&지급 관리/수금&지급 리스트"
        icon="bonds_dept"
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
        <CollectBuyListBox formik={formik} onReset={() => dispatch(resetCollectsBuysFilters())} />
        <CollectBuyStatusList
          filters={defaultFilters}
          onFilters={handleFilters}
        />
        <TaxbillTotalSummary totalPrice={totalPrice} />
        <TaxbillList
          columns={columns}
          rows={tableData}
          error={error}
          loading={loading}
        />
      </Box>
      <AskDialog
        visible={removeVisible}
        title="매출 취소"
        description={`해당 매출 "${selectedItem?.invTxt}" 을 취소하시겠습니까?`}
        onConfirm={onRemoveConfirm}
        onCancle={onRemoveCancle}
      />
    </>
  );
};

export default CollectBuyListContainer;
